import { useRef, useState, useMemo } from 'react';
import './Book3D.css';
import { Canvas, useFrame, useLoader } from '@react-three/fiber';
import { TextureLoader } from 'three';
import * as THREE from 'three';
import bookCover from './files/book_cover.png';

function Book({ mouse }) {
  const bookRef = useRef();
  const texture = useLoader(TextureLoader, bookCover);

  // Create radial gradient texture for vignette
  const vignetteTexture = useMemo(() => {
    const canvas = document.createElement('canvas');
    canvas.width = 512;
    canvas.height = 512;
    const ctx = canvas.getContext('2d');

    const gradient = ctx.createRadialGradient(256, 256, 0, 256, 256, 160);
    gradient.addColorStop(0, 'rgba(100, 100, 100, 0.25)');
    gradient.addColorStop(0.6, 'rgba(100, 100, 100, 0.12)');
    gradient.addColorStop(1, 'rgba(100, 100, 100, 0)');

    ctx.fillStyle = gradient;
    ctx.fillRect(0, 0, 512, 512);

    const tex = new THREE.CanvasTexture(canvas);
    return tex;
  }, []);

  // Base angle offset so book is slightly angled even without mouse interaction
  const baseAngleY = 0.3;
  const baseAngleX = -0.1;

  useFrame((state) => {
    if (bookRef.current) {
      // Parallax effect with mouse - reduced sensitivity, plus base angle
      const targetRotationY = baseAngleY + mouse.x * 0.25 + Math.sin(state.clock.elapsedTime * 0.3) * 0.1;
      const targetRotationX = baseAngleX + -mouse.y * 0.15 + Math.sin(state.clock.elapsedTime * 0.2) * 0.05;

      // Smooth interpolation
      bookRef.current.rotation.y += (targetRotationY - bookRef.current.rotation.y) * 0.05;
      bookRef.current.rotation.x += (targetRotationX - bookRef.current.rotation.x) * 0.05;

      // Subtle floating motion
      bookRef.current.position.y = Math.sin(state.clock.elapsedTime * 0.5) * 0.1;
    }
  });

  return (
    <group position={[0, 0, 0]}>
      {/* Radial gradient vignette behind book */}
      <mesh position={[0, 0, -1]} rotation={[0, 0, 0]}>
        <circleGeometry args={[4.6, 64]} />
        <meshBasicMaterial
          map={vignetteTexture}
          transparent
          depthWrite={false}
        />
      </mesh>

      <group ref={bookRef} scale={1.5} castShadow receiveShadow>
        {/* Front cover */}
        <mesh position={[0, 0, 0.16]} castShadow>
          <boxGeometry args={[2, 3.095, 0.05]} />
          <meshStandardMaterial
            map={texture}
            toneMapped={false}
          />
        </mesh>

        {/* Book spine */}
        <mesh position={[-1.025, 0, 0]} castShadow>
          <boxGeometry args={[0.05, 3.095, 0.35]} />
          <meshStandardMaterial color="#DC143C" roughness={0.4} metalness={0.1} />
        </mesh>

        {/* Pages - main block */}
        <mesh position={[0.02, 0, 0]} castShadow>
          <boxGeometry args={[1.95, 3.045, 0.3]} />
          <meshStandardMaterial color="#f8f8f8" roughness={0.8} />
        </mesh>

        {/* Page edges - right side */}
        <mesh position={[1.005, 0, 0]} castShadow>
          <boxGeometry args={[0.01, 3.045, 0.28]} />
          <meshStandardMaterial color="#e8e8e8" roughness={0.9} />
        </mesh>

        {/* Page edges - top */}
        <mesh position={[0.02, 1.5225, 0]} castShadow>
          <boxGeometry args={[1.93, 0.01, 0.28]} />
          <meshStandardMaterial color="#e8e8e8" roughness={0.9} />
        </mesh>

        {/* Page edges - bottom */}
        <mesh position={[0.02, -1.5225, 0]} castShadow>
          <boxGeometry args={[1.93, 0.01, 0.28]} />
          <meshStandardMaterial color="#e8e8e8" roughness={0.9} />
        </mesh>

        {/* Back cover */}
        <mesh position={[0, 0, -0.16]} castShadow>
          <boxGeometry args={[2, 3.095, 0.05]} />
          <meshStandardMaterial color="#DC143C" roughness={0.4} metalness={0.1} />
        </mesh>
      </group>
    </group>
  );
}

function Book3D() {
  const [mouse, setMouse] = useState({ x: 0, y: 0 });

  const handleMouseMove = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width) * 2 - 1;
    const y = ((e.clientY - rect.top) / rect.height) * 2 - 1;
    setMouse({ x, y });
  };

  return (
    <div
      className="book3d-container"
      onMouseMove={handleMouseMove}
    >
      <Canvas
        camera={{ position: [0, 0, 7], fov: 45 }}
        style={{ background: 'transparent', width: '100%', height: '100%' }}
        gl={{ antialias: true, alpha: true }}
        dpr={[1, 2]}
        shadows
      >
        <ambientLight intensity={1.5} />
        <directionalLight
          position={[5, 5, 5]}
          intensity={2}
          castShadow
          shadow-mapSize-width={2048}
          shadow-mapSize-height={2048}
        />
        <directionalLight position={[-5, 5, 5]} intensity={1} />
        <pointLight position={[0, 0, 10]} intensity={0.8} />
        <Book mouse={mouse} />
      </Canvas>
    </div>
  );
}

export default Book3D;
